import { useMemo } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAgentOverrider } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import { useAgentsPaginatedByAgentId } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	type StoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useAgentVersioningConfig } from '../agent-versioning-config/useAgentVersioningConfig';
import { getFirstMessageOfType } from '../chat-store/getFirstMessageOfType';
import { isEmptyConversation } from '../chat-store/isEmptyConversation';

import { isAgentUnavailable } from './isAgentUnavailable';
import type { UseConversationResult } from './UseConversationResult';

type ProvisionalConversationAgent = Pick<NonNullable<Conversation['agent']>, 'agent_id'> &
	Partial<
		Pick<
			NonNullable<Conversation['agent']>,
			| 'agent_named_id'
			| 'agent_name'
			| 'creator_type'
			| 'identity_account_id'
			| 'is_default'
			| 'execution_config'
		>
	>;

const createProvisionalSelectedAgent = (
	conversationAgent: ProvisionalConversationAgent | undefined,
): Agent | undefined => {
	const conversationAgentId = conversationAgent?.agent_id;
	if (!conversationAgentId) {
		return undefined;
	}

	const conversationAgentCreatorType = conversationAgent?.creator_type;

	return {
		id: conversationAgentId,
		named_id: conversationAgent?.agent_named_id || conversationAgentId,
		name: conversationAgent?.agent_name || conversationAgentId,
		description: null,
		creator_type: (conversationAgentCreatorType || 'CUSTOMER') as Agent['creator_type'],
		available_plugins: {},
		is_default: conversationAgent?.is_default ?? false,
		actor_type: 'AGENT',
		user_defined_conversation_starters: null,
		favourite: false,
		deactivated: false,
		identity_account_id: conversationAgent?.identity_account_id,
		execution_config: conversationAgent?.execution_config,
	};
};

export const useConversationData = ({
	conversationId,
	conversation,
	assistanceServiceParams,
}: {
	conversationId: string;
	conversation?: Conversation;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
}): UseConversationResult => {
	const fallbackParams = useAssistanceServiceParams({
		touchpointSource: 'useConversationHook',
	});
	const serviceParams = assistanceServiceParams || fallbackParams;
	const [messages] = useChatMessages({
		conversationId,
	});

	const { agentDefaultIdOverride } = useAgentOverrider();

	// Read only the specific conversation fields the derivation needs as locals,
	// so the memo below depends on these primitives rather than the whole
	// `conversation` object. A whole-object dependency would re-run the memo (and
	// the downstream `useAgentsPaginatedByAgentId` fetch) on unrelated per-turn
	// conversation writes during streaming, even though neither the gate-on nor
	// gate-off path reads those fields here.
	const conversationBackendAgentId = conversation?.agent?.agent_id;
	const conversationSourceAgentId = conversation?.sourceAgentId;

	const selectedAgentId = useMemo(() => {
		if (isExperimentEnabled('rovo_chat_mention_agents')) {
			// CONVERSATION identity shown in the agent selector header. Prefer the
			// canonical backend/store conversation agent when present. Before the first
			// turn is sent, use the surface-level default agent so that first turn is
			// routed to it and the backend persists it as the conversation agent.
			return conversationBackendAgentId || agentDefaultIdOverride || undefined;
		}

		const firstAssistantAuthorId = getFirstMessageOfType<StoreAssistant>(
			messages ?? [],
			'ASSISTANT',
		)?.content.message.message.author_id;
		const agentIdFromConversation = conversationSourceAgentId ?? firstAssistantAuthorId;

		if (fg('rovo_chat_fix_agent_selection_after_cancel')) {
			// fixing the fallback to correctly select an agent if a user cancels a session
			return agentIdFromConversation ?? conversationBackendAgentId ?? agentDefaultIdOverride;
		}

		return agentIdFromConversation ?? agentDefaultIdOverride;
	}, [agentDefaultIdOverride, conversationSourceAgentId, conversationBackendAgentId, messages]);

	const { agent, error: agentError } = useAgentsPaginatedByAgentId({
		agentId: selectedAgentId,
		assistanceServiceParams: serviceParams,
	});

	const agentErrorCode = agentError?.errorAttributes?.errorCode;

	/**
	 * if agent fetch failed with status code AGENT_FORBIDDEN or AGENT_DEACTIVATED, extract agent name from messages because user has no access to the agent endpoint anymore
	 * scenario: user has access to an agent, created a conversation with it, but then lost access to the agent, this will be triggered when user tries to reopen the conversation;
	 * scenario: user has access to an agent, created a conversation with it, but then the agent was deactivated, this will be triggered when user tries to reopen the conversation;
	 */
	const fallbackAgent = useMemo(() => {
		if (!fg('convo-ai-enable-agents-use-permission')) {
			return undefined;
		}

		// Don't create fallback agent for empty conversations (new chats)
		if (isEmptyConversation(conversationId) || !selectedAgentId) {
			return undefined;
		}

		// Only create fallback agent when agent fetch failed (to extract agent name from messages for display)
		// If agent was successfully fetched, no need for fallback
		if (!isAgentUnavailable(agentErrorCode)) {
			return undefined;
		}

		// Try to extract agent metadata from the first assistant message
		const firstAssistantMessage = getFirstMessageOfType<StoreAssistant>(
			messages ?? [],
			'ASSISTANT',
		);
		const author = firstAssistantMessage?.content.message.message.author;

		if (!author?.name) {
			return undefined;
		}

		// Create a minimal Agent object with only required fields from message metadata
		// This allows the UI to display the agent name even when access is denied
		return {
			id: selectedAgentId,
			named_id: author.named_id ?? selectedAgentId,
			name: author.name,
			description: null,
			creator_type: 'CUSTOMER' as const,
			available_plugins: {},
			is_default: false,
			actor_type: 'AGENT' as const,
			user_defined_conversation_starters: null,
			favourite: false,
			deactivated: false,
		} satisfies Agent;
	}, [selectedAgentId, messages, agentErrorCode, conversationId]);

	const conversationAgentNamedId = conversation?.agent?.agent_named_id;
	const conversationAgentName = conversation?.agent?.agent_name;
	const conversationAgentCreatorType = conversation?.agent?.creator_type;
	const conversationAgentIdentityAccountId = conversation?.agent?.identity_account_id;
	const conversationAgentIsDefault = conversation?.agent?.is_default;
	const conversationAgentExecutionTier = conversation?.agent?.execution_config?.execution_tier;
	const provisionalSelectedAgent = useMemo(() => {
		if (!isExperimentEnabled('rovo_chat_mention_agents') || !conversationBackendAgentId) {
			return undefined;
		}

		const conversationAgentForFallback: ProvisionalConversationAgent = {
			agent_id: conversationBackendAgentId,
		};

		if (conversationAgentNamedId) {
			conversationAgentForFallback.agent_named_id = conversationAgentNamedId;
		}

		if (conversationAgentName) {
			conversationAgentForFallback.agent_name = conversationAgentName;
		}

		if (conversationAgentCreatorType) {
			conversationAgentForFallback.creator_type = conversationAgentCreatorType;
		}

		if (conversationAgentIdentityAccountId) {
			conversationAgentForFallback.identity_account_id = conversationAgentIdentityAccountId;
		}

		if (typeof conversationAgentIsDefault === 'boolean') {
			conversationAgentForFallback.is_default = conversationAgentIsDefault;
		}

		if (conversationAgentExecutionTier) {
			conversationAgentForFallback.execution_config = {
				execution_tier: conversationAgentExecutionTier,
			};
		}

		return createProvisionalSelectedAgent(conversationAgentForFallback);
	}, [
		conversationBackendAgentId,
		conversationAgentNamedId,
		conversationAgentName,
		conversationAgentCreatorType,
		conversationAgentIdentityAccountId,
		conversationAgentIsDefault,
		conversationAgentExecutionTier,
	]);
	const selectedAgent = agent ?? fallbackAgent ?? provisionalSelectedAgent;
	const isFetching =
		isExperimentEnabled('rovo_chat_mention_agents') &&
		Boolean(selectedAgentId && !selectedAgent && !agentError);

	// Calculate agentIdForNewChat: only use valid agents (not fallback/error state) for new chats
	const agentIdForNewChat = useMemo(() => {
		if (!fg('convo-ai-enable-agents-use-permission')) {
			return selectedAgent?.id;
		}

		// No agent selected
		if (!selectedAgent?.id) {
			return undefined;
		}

		// If agent fetch failed with forbidden/deactivated error, don't use it for new chats
		if (isAgentUnavailable(agentErrorCode)) {
			return undefined;
		}

		// If conversation has an error state, the agent is a fallback - don't use it for new chats
		if (conversation?.status === CHAT_STORE_STATUS.deactivatedAgentError) {
			return undefined;
		}

		// Valid agent - safe to use for new chats
		return selectedAgent?.id;
	}, [agentErrorCode, selectedAgent?.id, conversation?.status]);

	const agentVersioningConfig = useAgentVersioningConfig();

	const agentVersion = useMemo(() => {
		if (!fg('rovo_agent_versioning_enabled')) {
			return undefined;
		}

		// Prefer version stored on conversation (from chat-new event)
		if (conversation?.agentVersion) {
			return conversation.agentVersion;
		}

		// Fall back to version from first assistant message author (from BE response)
		const firstAssistantMessage = getFirstMessageOfType<StoreAssistant>(
			messages ?? [],
			'ASSISTANT',
		);
		const author = firstAssistantMessage?.content.message.message.author;

		if (author?.versionId || author?.versionType) {
			return {
				versionId: author.versionId,
				versionType: author.versionType,
				versionNumber: author.versionNumber,
			};
		}

		return undefined;
	}, [conversation?.agentVersion, messages]);

	const agentVersionForNewChat = useMemo(() => {
		if (!agentVersioningConfig?.preserveVersionOnNewChat) {
			return undefined;
		}
		return agentVersion;
	}, [agentVersioningConfig?.preserveVersionOnNewChat, agentVersion]);

	return {
		conversation,
		selectedAgent,
		selectedAgentId,
		isFetching,
		agentErrorCode,
		agentIdForNewChat,
		agentVersion,
		agentVersionForNewChat,
	};
};
